/**
 * Enterprise Onboarding API Service
 *
 * Handles complete enterprise onboarding flow including:
 * - Organization creation
 * - SSO configuration
 * - Team member provisioning
 * - Compliance settings
 * - Feature entitlements
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call, APIResponse } from './utils';
import { API_ENDPOINTS } from './endpoints';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Team member definition for onboarding
 */
export interface TeamMember {
  email: string;
  role: 'admin' | 'user' | 'viewer';
}

/**
 * SSO Configuration
 */
export interface SSOConfiguration {
  provider: string;
  entity_id: string;
  metadata_url: string;
}

/**
 * Compliance Settings
 */
export interface ComplianceSettings {
  gdpr_enabled: boolean;
  ccpa_enabled: boolean;
  soc2_enabled: boolean;
  hipaa_enabled: boolean;
}

/**
 * Complete onboarding request data
 * Maps from EnterpriseOnboarding.tsx form state to API format
 */
export interface OnboardingRequest {
  // Organization info (required)
  Client_Name: string;
  Client_Type: 'OSS' | 'PRO' | 'ENTERPRISE';
  Industry?: string;
  Team_Size?: string;

  // Admin user (required)
  Admin_Email: string;
  Admin_Name?: string;

  // SSO configuration (optional)
  SSO_Configuration?: SSOConfiguration | null;

  // Team members (optional)
  Team_Members?: TeamMember[];

  // Compliance settings (optional)
  Compliance_Settings?: ComplianceSettings;

  // Feature entitlements (optional)
  Selected_Features?: string[];
}

/**
 * Created client information
 */
export interface ClientInfo {
  Client_ID: string;
  Client_Name: string;
  Client_Type: string;
  Client_Status: string;
  Monthly_Revenue_USD: number;
  Create_Date: string;
}

/**
 * Admin user information
 */
export interface AdminUserInfo {
  Email_Address: string;
  User_Display_Name: string;
  entitlement: {
    Email_Address: string;
    Client_ID: string;
    Company_ID: string | null;
    Admin: boolean;
    Developer: boolean;
    Viewer: boolean;
  };
}

/**
 * Onboarding status information
 */
export interface OnboardingStatus {
  status: 'complete' | 'pending' | 'failed';
  client_id: string;
  admin_configured: boolean;
  company_configured: boolean;
  sso_configured?: boolean;
  compliance_configured?: boolean;
  features_configured?: boolean;
  team_members_added?: number;
  next_steps: string[];
}

/**
 * Complete onboarding response
 */
export interface OnboardingResponse {
  client: ClientInfo;
  admin_user: AdminUserInfo;
  company: any | null;
  onboarding_status: OnboardingStatus;
}

/**
 * Onboarding API response wrapper
 * Follows APIResponse<T> with Records array pattern
 */
export interface OnboardingAPIResponse {
  success: boolean;
  message: string;
  meta: {
    Request_ID: string;
    Timestamp: string;
    Client_ID?: string;
    Client_Name?: string;
    Client_Type?: string;
    Monthly_Revenue_USD?: number;
  };
  data: {
    Records: OnboardingResponse[];
  };
}

// ============================================================================
// API Service Functions
// ============================================================================

/**
 * Generate Client_ID from organization name
 * Pattern: UPPERCASE with underscores, max 128 chars
 *
 * @param organizationName - Human-readable organization name
 * @returns Client_ID suitable for database
 */
function generateClientId(organizationName: string): string {
  return organizationName
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, 128);
}

/**
 * Map selected plan to backend Client_Type
 */
function mapPlanToClientType(selectedPlan: string): 'OSS' | 'PRO' | 'ENTERPRISE' {
  switch (selectedPlan) {
    case 'free':
      return 'OSS';
    case 'starter':
      return 'OSS'; // Starter is still OSS tier with paid features
    case 'professional':
      return 'PRO';
    case 'enterprise':
      return 'ENTERPRISE';
    default:
      return 'PRO'; // Default to PRO if unknown
  }
}

/**
 * Map form data to backend API format
 *
 * @param formData - Form state from EnterpriseOnboarding component
 * @returns OnboardingRequest formatted for backend API
 */
function mapFormDataToRequest(formData: {
  organizationName: string;
  industry: string;
  teamSize: string;
  ssoProvider: string;
  ssoEntityId: string;
  ssoMetadataUrl: string;
  teamMembers: TeamMember[];
  gdprEnabled: boolean;
  ccpaEnabled: boolean;
  soc2Enabled: boolean;
  hipaaEnabled: boolean;
  selectedFeatures: string[];
  selectedPlan: string;
}): OnboardingRequest {
  // Determine tier based on selected plan
  const clientType = mapPlanToClientType(formData.selectedPlan);

  const request: OnboardingRequest = {
    Client_Name: formData.organizationName,
    Client_Type: clientType,
    Industry: formData.industry || undefined,
    Team_Size: formData.teamSize || undefined,
    Admin_Email: formData.teamMembers[0]?.email || '', // First team member is admin
    Admin_Name: formData.organizationName,
  };

  // Add SSO configuration if provided
  if (formData.ssoProvider && formData.ssoEntityId && formData.ssoMetadataUrl) {
    request.SSO_Configuration = {
      provider: formData.ssoProvider,
      entity_id: formData.ssoEntityId,
      metadata_url: formData.ssoMetadataUrl,
    };
  }

  // Add team members (excluding first one who becomes admin)
  if (formData.teamMembers.length > 1) {
    request.Team_Members = formData.teamMembers.slice(1);
  }

  // Add compliance settings if any are enabled
  const hasCompliance = formData.gdprEnabled || formData.ccpaEnabled ||
                         formData.soc2Enabled || formData.hipaaEnabled;
  if (hasCompliance) {
    request.Compliance_Settings = {
      gdpr_enabled: formData.gdprEnabled,
      ccpa_enabled: formData.ccpaEnabled,
      soc2_enabled: formData.soc2Enabled,
      hipaa_enabled: formData.hipaaEnabled,
    };
  }

  // Add selected features
  if (formData.selectedFeatures.length > 0) {
    request.Selected_Features = formData.selectedFeatures;
  }

  return request;
}

/**
 * Create a new client with complete enterprise onboarding
 *
 * This function handles the complete onboarding flow:
 * 1. Creates client record
 * 2. Creates admin user and entitlement
 * 3. Configures SSO (if provided)
 * 4. Provisions team members (if provided)
 * 5. Sets compliance settings (if provided)
 * 6. Creates feature entitlements (if provided)
 *
 * @param formData - Complete form data from EnterpriseOnboarding component
 * @returns Promise<APIResponse<OnboardingAPIResponse>> - Complete onboarding response
 *
 * @example
 * const result = await createClient(formData);
 * if (result.success) {
 *   const onboarding = result.data.data.Records[0];
 *   console.log('Client created:', onboarding.client.Client_ID);
 *   console.log('Admin user:', onboarding.admin_user.Email_Address);
 *   console.log('Next steps:', onboarding.onboarding_status.next_steps);
 * } else {
 *   console.error('Onboarding failed:', result.message);
 * }
 */
export async function createClient(formData: {
  organizationName: string;
  industry: string;
  teamSize: string;
  ssoProvider: string;
  ssoEntityId: string;
  ssoMetadataUrl: string;
  teamMembers: TeamMember[];
  gdprEnabled: boolean;
  ccpaEnabled: boolean;
  soc2Enabled: boolean;
  hipaaEnabled: boolean;
  selectedFeatures: string[];
  selectedPlan: string;
}): Promise<APIResponse<OnboardingAPIResponse>> {
  try {
    // Validate required fields
    if (!formData.organizationName) {
      return {
        success: false,
        data: {} as OnboardingAPIResponse,
        message: 'Organization name is required'
      };
    }

    if (!formData.teamMembers || formData.teamMembers.length === 0) {
      return {
        success: false,
        data: {} as OnboardingAPIResponse,
        message: 'At least one admin team member is required'
      };
    }

    // Map form data to API request format
    const requestData = mapFormDataToRequest(formData);

    // Generate Client_ID for the request
    const Client_ID = generateClientId(formData.organizationName);

    // Add Client_ID to request
    const requestBody = {
      ...requestData,
      Client_ID,
    };

    console.log('Creating client with onboarding data:', requestBody);

    // Call self-service onboarding API (no superadmin required)
    const result = await Make_Authorized_API_Call<OnboardingAPIResponse>(
      API_ENDPOINTS.ONBOARDING.SELF_SERVICE,
      'POST',
      requestBody
    );

    return result;

  } catch (error) {
    console.error('Client onboarding error:', error);
    return {
      success: false,
      data: {} as OnboardingAPIResponse,
      message: error instanceof Error ? error.message : 'An unexpected error occurred during onboarding'
    };
  }
}

/**
 * Validate email format
 *
 * @param email - Email address to validate
 * @returns true if valid email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate SSO metadata URL format
 *
 * @param url - Metadata URL to validate
 * @returns true if valid HTTPS URL
 */
export function validateMetadataUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validate SSO entity ID format
 *
 * @param entityId - Entity ID to validate
 * @returns true if valid format (URL or URN)
 */
export function validateEntityId(entityId: string): boolean {
  // Entity ID can be a URL or URN
  try {
    new URL(entityId);
    return true;
  } catch {
    // Check if it's a valid URN format
    return /^urn:[a-z0-9][a-z0-9-]{0,31}:[a-z0-9()+,\-.:=@;$_!*'%/?#]+$/i.test(entityId);
  }
}

// ============================================================================
// SSO Connection Testing
// ============================================================================

/**
 * SSO test result for a single validation check
 */
export interface SSOTestDetail {
  test: string;
  status: 'PASS' | 'FAIL';
  message: string;
}

/**
 * Complete SSO connection test result
 */
export interface SSOTestResult {
  is_valid: boolean;
  entity_id_match: boolean;
  certificate_valid: boolean;
  certificate_expiry: string | null;
  metadata_parsed: boolean;
  issuer: string | null;
  sso_url: string | null;
  tests_passed: number;
  tests_total: number;
  test_details: SSOTestDetail[];
}

/**
 * Test SSO connection configuration
 *
 * Validates SAML SSO setup by:
 * 1. Fetching and validating metadata URL
 * 2. Parsing SAML XML metadata
 * 3. Verifying entity ID matches
 * 4. Checking certificate presence and validity
 *
 * @param entityId - SAML Entity ID
 * @param metadataUrl - SAML Metadata URL (must be HTTPS)
 * @returns Promise<APIResponse<SSOTestResult>> - Detailed test results
 *
 * @example
 * const result = await testSSOConnection(
 *   'https://sso.example.com/saml',
 *   'https://sso.example.com/saml/metadata'
 * );
 * if (result.success && result.data.data?.Records?.[0]) {
 *   const testResult = result.data.data.Records[0];
 *   if (testResult.is_valid) {
 *     console.log('SSO configuration is valid');
 *   } else {
 *     console.log(`Failed tests:`, testResult.test_details.filter(t => t.status === 'FAIL'));
 *   }
 * }
 */
export async function testSSOConnection(
  entityId: string,
  metadataUrl: string
): Promise<APIResponse<{ data: { Records: SSOTestResult[] } }>> {
  try {
    // Validate inputs
    if (!entityId) {
      return {
        success: false,
        data: {} as any,
        message: 'Entity ID is required'
      };
    }

    if (!metadataUrl) {
      return {
        success: false,
        data: {} as any,
        message: 'Metadata URL is required'
      };
    }

    // Validate entity ID format
    if (!validateEntityId(entityId)) {
      return {
        success: false,
        data: {} as any,
        message: 'Invalid Entity ID format (must be URL or URN)'
      };
    }

    // Validate metadata URL format
    if (!validateMetadataUrl(metadataUrl)) {
      return {
        success: false,
        data: {} as any,
        message: 'Metadata URL must be a valid HTTPS URL'
      };
    }

    console.log('[SSO Test] Testing SSO connection:', {
      entityId,
      metadataUrl
    });

    // Call backend API
    const result = await Make_Authorized_API_Call<{ data: { Records: SSOTestResult[] } }>(
      API_ENDPOINTS.SUPERADMIN.SSO_TEST,
      'POST',
      {
        Entity_ID: entityId,
        Metadata_URL: metadataUrl
      }
    );

    return result;

  } catch (error) {
    console.error('[SSO Test] Error:', error);
    return {
      success: false,
      data: {} as any,
      message: error instanceof Error ? error.message : 'An unexpected error occurred during SSO testing'
    };
  }
}
